import * as React from 'react';
import { uuid } from 'uuidv4';
import { ConfirmationDialogTrigger } from '../../../components/dialog-types/ConfirmationDialogTrigger';
import { DeselectAll, SelectableGroup } from '../../../components/drag-select';
import { TimeInput } from '../../../components/form-elements/_index';
import { WeekDays } from '../../../models/enums/week-days';
import { DeliveryHourView } from '../../../shared/models/ship-to-address-read';
import { AppUtils } from '../../../utils/app-utils';
import '../../styles.scss';
import AddressHourBox from './AddressHourBox';

interface IProps {
    deliveryHours: DeliveryHourView[];
    onUpdate: any;
    onRemove: any;
    onDisplayUpdate: any;
    id: any;
    showFixedRange: boolean;
    deliveryDays: number;
    disabled: boolean;
}

interface IHourItem {
    uniqueId: string;
    index: number;
    hour: number;
    minute: number;
    selected: boolean;
}

interface IFriendlyHour {
    start: string;
    end: string;
    map: any;
}

interface IWeekdayModel {
    dayOfWeek: WeekDays;
    shortName: string;
    id: string;
    selected: boolean;
}

export const EditAddressHoursControl = (props : IProps) => {

    const IS_DEBUG = false;
    let IGNORE_UNMOUNT_SELECTABLE = false;

    // Build the 7 day boxes
    const buildDayOptions = (groupId : any) : IWeekdayModel[] => {
        return [
            {
                dayOfWeek: 1 as WeekDays,
                shortName: 'U',
                id: `sunday-switch-${ groupId }`,
                selected: false
            },
            {
                dayOfWeek: 2 as WeekDays,
                shortName: 'M',
                id: `monday-switch-${ groupId }`,
                selected: false
            },
            {
                dayOfWeek: 4 as WeekDays,
                shortName: 'T',
                id: `tuesday-switch-${ groupId }`,
                selected: false
            },
            {
                dayOfWeek: 8 as WeekDays,
                shortName: 'W',
                id: `wednesday-switch-${ groupId }`,
                selected: false
            },
            {
                dayOfWeek: 16 as WeekDays,
                shortName: 'R',
                id: `thursday-switch-${ groupId }`,
                selected: false
            },
            {
                dayOfWeek: 32 as WeekDays,
                shortName: 'F',
                id: `friday-switch-${ groupId }`,
                selected: false
            },
            {
                dayOfWeek: 64 as WeekDays,
                shortName: 'S',
                id: `saturday-switch-${ groupId }`,
                selected: false
            }
        ];
    }; 

    // Build the 48 Timeslots
    const timeSlots : IHourItem[] = Array.from({ length: 24 * 60 / 30 }, (v : any, index: number) => {

        const hour        = Math.floor(index * 30 / 60);
        const minute    = index * 30 - hour * 60;
    
        return { 
            uniqueId: index.toString(),
            index,
            hour,
            minute,
            selected: false
        };
    
    });

    const [tmpSelectedHours, setTmpSelectedHours]   = React.useState([] as IHourItem[]);
    const [friendlyHours, setFriendlyHours]         = React.useState([] as IFriendlyHour[]);
    const [editTimeSlots, setEditTimeSlots]         = React.useState([] as any);
    const [editDays, setEditDays]                   = React.useState(buildDayOptions(props.id));
    
    const [isFixedRange, setIsFixedRange]           = React.useState(props.showFixedRange);
    const [initFlag, setInitFlag]                   = React.useState(0);
    const [fixedStart, setFixedStart]               = React.useState<Date>();

    /**
     * Update state based on server's delivery hours
     */
    React.useEffect(() => {

        if (props.deliveryHours) {
            
            const tmpSelectedDays = [...editDays];
            
            props.deliveryHours.forEach((itm : DeliveryHourView) => {

                if (!isFixedRange || isFixedRange === undefined) {
                    const startTimeparts    = itm.startTime.split(':');
                    const startHours        = Number.parseInt(startTimeparts[0], 10);
                    const startMinutes      = Number.parseInt(startTimeparts[1], 10);

                    const endTimeparts      = itm.endTime.split(':');
                    const endHours          = Number.parseInt(endTimeparts[0], 10);
                    const endMinutes        = Number.parseInt(endTimeparts[1], 10);

                    let foundFirst          = false;

                    timeSlots.forEach((tsItm : any) => {

                        tsItm.uniqueID = props.id;

                        // Hours and Minutes Match the start
                        // This is the first entry in our group
                        if ((tsItm.hour === startHours) && 
                            (tsItm.minute === startMinutes)) {
                                tsItm.selected = true;
                                foundFirst = true;
                                return;
                        }

                        if (foundFirst) {

                            if ((tsItm.hour >= startHours) && 
                                (tsItm.hour < endHours) && 
                                (tsItm.minute === 30 && endMinutes === 0)) {
                                    tsItm.selected = true;
                                    return;
                            }

                            // end hours match
                            // end minutes is 30 and the ts minute is 0;
                            if ((tsItm.hour === endHours) &&
                                (endMinutes === 30) &&
                                (tsItm.minute === 0)) {
                                    tsItm.selected = true;
                                    return;
                            }

                            // If the current hour is greater than the start hour
                            // and less than the end hour this falls in the range
                            // startHours = 4
                            // tsItm.hour = 5
                            // endHours = 13
                            if ((tsItm.hour >= startHours) && 
                                (tsItm.hour < endHours) && 
                                (tsItm.minute !== endMinutes)) {
                                    tsItm.selected = true;
                                    return;
                            }

                            // If the current hour is greater than the start hour
                            // and less than the end hour this falls in the range
                            // startHours = 4
                            // tsItm.hour = 5
                            // endHours = 13
                            if ((tsItm.hour >= startHours) && 
                                (tsItm.hour < endHours)) {
                                    tsItm.selected = true;
                                    return;
                            }
                        }

                    });
                }
            });

            // update the day boxes
            tmpSelectedDays.forEach((tempDay : IWeekdayModel) => {
                // tslint:disable-next-line: no-bitwise
                tempDay.selected = (props.deliveryDays & tempDay.dayOfWeek ) ? true : false;
            });

            if (!isFixedRange) {
                setEditDays(tmpSelectedDays);
                setEditTimeSlots(timeSlots);

                // Popualte the selected values
                const selectedTimes = timeSlots.filter((itm: IHourItem) => {
                    return itm.selected;
                });

                setTmpSelectedHours(selectedTimes);
            }
            else {
                if (props.deliveryHours &&
                    props.deliveryHours.length > 0 &&
                    (props.deliveryHours[0].startTime === props.deliveryHours[0].endTime)) {
                    
                    const st = props.deliveryHours[0].startTime.split(':');
                    
                    const d = new Date();
                    d.setHours(Number.parseInt(st[0], 10));
                    d.setMinutes(Number.parseInt(st[1], 10));

                    setFixedStart(d);
                }
            }
        }
        else {
            console.log('Skipping prop update because the length is the same');
        }
        
    }, [props.deliveryHours.length]);

    /**
     * Update the parent with friendly hours
     */
    React.useEffect(() => {
        props.onDisplayUpdate(props.id, friendlyHours);
    }, [friendlyHours]);

    /**
     * Re-create groups when the tmp hour set changes
     */
    React.useEffect(() => {
        if (tmpSelectedHours.length > 0) {
            createHourGroupsForRange();
        }
    }, [tmpSelectedHours]);

    /**
     * Update fixed hour groups when start time changes
     */
    React.useEffect(() => {
        if (isFixedRange) {
            createHourGroupsForFixed();
        }
    }, [fixedStart]);
    
    /**
     * Flip the UI when the fixed mode range is changed
     */
    React.useEffect(() => {
        
        if (initFlag > 0) {
            handleClearAll();
            
            const ts : IHourItem[] = Array.from({ length: 24 * 60 / 30 }, (v : any, index: number) => {

                const hour        = Math.floor(index * 30 / 60);
                const minute    = index * 30 - hour * 60;
            
                return { 
                    uniqueId: index.toString(),
                    index,
                    hour,
                    minute,
                    selected: false
                };
            
            });

            IGNORE_UNMOUNT_SELECTABLE = true;
            setEditTimeSlots(ts);
            setTmpSelectedHours([]);
            setIsFixedRange(props.showFixedRange);
        }
        else {
            setInitFlag(1);
        }
        
    }, [props.showFixedRange]);

    const buildFlags = () => {
        const selectedDays = editDays.filter((itm : any) => itm.selected );
        return selectedDays.reduce((accum : number, curr : IWeekdayModel) => {
            if (curr.selected) {
                // tslint:disable-next-line: no-bitwise
                accum = accum | curr.dayOfWeek;
            }
            return accum;
        }, 0);
    };

    /**
     * Callback for when the user is done selecting items
     * @param evt List of selected items
     */
    const handleSelectionFinish = (evt : any[]) => {
        if (IGNORE_UNMOUNT_SELECTABLE) {
            return;
        }

        const selectedTimes = evt.reduce((acc: IHourItem[], itm : any) : IHourItem[] => {
            acc.push(itm.props.hourItem);
            return acc;
        }, [] as IHourItem[]);

        if (IS_DEBUG) {
            console.log("Selected Times were:");
            console.table(selectedTimes);
        }
        setTmpSelectedHours(selectedTimes);
    };

    /**
     * Toggle the selected property for the day index
     * provided
     * @param idx Day index to toggle
     */
    const toggleDay = (idx : number) => {

        if (props.disabled) {
            return;
        }

        const tmp = [...editDays];
        tmp.forEach((itm : IWeekdayModel) => {
            if (itm.dayOfWeek === idx) {
                itm.selected = !itm.selected;
            }
        });
        
        setEditDays(tmp);

        if (isFixedRange) {
            createHourGroupsForFixed();
        }
        else {
            createHourGroupsForRange();
        }
        
    };

    /**
     * Handle time change events
     * @param date The newly selected time
     */
    const onTimeChange = (date : Date) => {
        setFixedStart(date);
        createHourGroupsForFixed();
    };

    /**
     * Respond to the clear button by resetting the state
     */
    const handleClearAll = () => {
        const tmpDays = [...editDays];
        tmpDays.forEach((itm: any) => {
            itm.selected = false;
        });

        setFriendlyHours([]);
        setEditDays(tmpDays);
        props.onUpdate(props.id, []);
    };
      
    /**
     * Convert the individual time slots to timespan
     * like elements for the server
     */
    const createHourGroupsForRange = () => {

        const selectedDays = editDays.filter((itm : any) => itm.selected );
        if (selectedDays.length === 0) {
            console.log('Skipping group calculations because we have no days selected');
            return;
        }
        else if (tmpSelectedHours.length === 0) {
            console.log('Skipping group calculations because we have no times selected');
            return;
        }

        const selectedTimes                             = [...tmpSelectedHours];
        const selectedTimeRanges: DeliveryHourView[]    = [];
        let currentIndex                                = -1;
        let currentViewUniqueId                         = '';
        let currentTimeRange : DeliveryHourView;

        if (IS_DEBUG) {
            console.log('Creating Hour Groups, the current selected days are: ');
            console.log(selectedDays);
        }
        // Sort the entries based on index
        selectedTimes.sort((itm1: any, itm2: any) => {
            if (itm1.index < itm2.index) {
                return -1;
            }
            if (itm1.index > itm2.index) {
                return 1;
            }
            return 0;
        });

        // Generate the full ranges based on individual time
        // slots from the control
        selectedTimes.forEach((hourItem: any) => {

            const hourStr       = (hourItem.hour < 10) ? ('0' + hourItem.hour) : hourItem.hour.toString();
            const minuteStr     = (hourItem.minute < 10) ? ('0' + hourItem.minute) : hourItem.minute.toString();
            const idx           = hourItem.index;
            
            const startTime     = hourStr + ':' + minuteStr;
            const endTime       =  _formatEndTime(hourItem);

            const startTimespan = _convertToTimeSpan(startTime);
            const endTimespan   = _convertToTimeSpan(endTime);

            if (IS_DEBUG) {
                console.log("Looping through selected times, the current hourItem is: ");
                console.log(hourItem);
            }   

            if (currentIndex === -1) {
                currentIndex = idx;

                currentViewUniqueId = uuid(); // we need to track this view id for later

                const tmpasdf = new DeliveryHourView();
                tmpasdf.startTime = startTimespan;
                tmpasdf.endTime = endTimespan;
                tmpasdf.uniqueID = currentViewUniqueId;
                tmpasdf.weekDays = buildFlags();
                currentTimeRange = tmpasdf;

                selectedTimeRanges.push(currentTimeRange);
            }
            else if ((currentIndex + 1) === idx) {
                currentIndex++;

                selectedTimeRanges.forEach((dvItm : DeliveryHourView) => {
                    if (dvItm.uniqueID === currentViewUniqueId) {
                        dvItm.endTime = endTimespan;
                    }
                });
            }
            else {
                currentIndex = idx;
                currentViewUniqueId = uuid();

                const tmpqwer = new DeliveryHourView();
                tmpqwer.startTime = startTimespan;
                tmpqwer.endTime = endTimespan;
                tmpqwer.uniqueID = currentViewUniqueId;
                tmpqwer.weekDays = buildFlags();

                currentTimeRange = tmpqwer;

                selectedTimeRanges.push(currentTimeRange);
            }
        });

        // Format the grouped items into friendly diplay variants
        const grouped = AppUtils.groupHoursByDayOfWeek(selectedTimeRanges);
        setFriendlyHours(grouped);

        // Let the parent know the updated groups
        props.onUpdate(props.id, selectedTimeRanges, buildFlags());

        if (IS_DEBUG) {
            console.log("Friendly Hours Are:");
            console.table(grouped);

            console.log("Selected Times Are:");
            console.table(selectedTimeRanges);
        }
    };
    
    /**
     * Convert the individual time slots to timespan
     * like elements for the server
     */
    const createHourGroupsForFixed = () => {

        const selectedDays = editDays.filter((itm : any) => itm.selected );
        if (selectedDays.length === 0) {
            console.log('Skipping group calculations because we have no days selected');
            return;
        }
        else if (!fixedStart) {
            console.log('Skipping group calculations because we have no start time selected');
            return;
        }

        if (IS_DEBUG) {
            console.log('Creating Hour Groups, the current selected days are: ');
            console.log(selectedDays);

            console.log('Creating Hour Groups, the current start time is: ');
            console.log(fixedStart);
        }

        const h = fixedStart.getHours();
        const hstr = h < 10 ? `0${h}` : h; 

        const m = fixedStart.getMinutes();
        const mstr = m < 10 ? `0${m}` : m;

        const start = `${hstr}:${mstr}:00`;
        
        const timeOptions : DeliveryHourView[] = [];
        timeOptions.push({
            weekDays: buildFlags(),
            startTime: start,
            endTime: start,
            uniqueID: uuid()
        });     
        
        // Format the grouped items into friendly diplay variants
        const grouped = AppUtils.groupHoursByDayOfWeek(timeOptions);
        setFriendlyHours(grouped);

        // Let the parent know the updated groups
        props.onUpdate(props.id, timeOptions);

        if (IS_DEBUG) {
            console.log("Friendly Hours Are:");
            console.table(grouped);

            console.log("Selected Times Are:");
            console.table(timeOptions);
        }
    };

    /**
     * Format the end time for a given time interval
     * @param hourItem Hour string to format
     */
    const _formatEndTime = (hourItem: any) => {
        if (hourItem.minute === 30) {
            const h = hourItem.hour + 1;
            const hourStr = (h < 10) ? ('0' + h) : h.toString(); // 00
    
            return hourStr + ':00';
        }
        else {
            const hourStr = (hourItem.hour < 10) ? ('0' + hourItem.hour) : hourItem.hour.toString(); // 00
            return hourStr + ':30';
        }
    };

    /**
     * Convert the 12 hours time format to 24 hour format
     * @param time 
     */
    const _convertToTimeSpan = (time: any) => {
        const [sHours, minutes] = time.match(/([0-9]{1,2}):([0-9]{2})/).slice(1);
        return `${('0' + sHours).slice(-2)}:${minutes}:00`;
    };

    return <React.Fragment>
            <div className="row divider-row edit-address-hours-ctrl">
                <div className="col">                   
                    {
                        !isFixedRange 
                            ? <React.Fragment>
                                <div className="row">
                                    <div className="col">
                                        <SelectableGroup
                                            className="main"
                                            clickClassName="tick"
                                            enableDeselect
                                            disabled={ props.disabled }
                                            tolerance={0}
                                            globalMouse={true}
                                            allowClickWithoutSelected={false}
                                            onSelectionClear={ handleClearAll }
                                            onSelectionFinish={ handleSelectionFinish }>
                                            <DeselectAll className="selectable-button">
                                                {
                                                    props.disabled
                                                        ? null 
                                                        : <button 
                                                            className="btn-clear mb-3">
                                                            <i className="la la-ban"/>
                                                            Clear
                                                        </button>
                                                }                                                
                                            </DeselectAll>
                                            <div className="hour-boxes">
                                                {
                                                    editTimeSlots.map((itm: any, idx: number) => (
                                                        <AddressHourBox
                                                            key={ itm.uniqueId }
                                                            isSelected={ itm.selected }
                                                            hourItem={itm} />
                                                    ))
                                                }
                                            </div>
                                        </SelectableGroup>  
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col period-marker">
                                        PM
                                    </div>
                                </div>
                            </React.Fragment>
                            :  null
                    }

                    <div className="row">

                        {
                            isFixedRange 
                                ? <div className="col">
                                    <TimeInput 
                                        label="Select Start Time"
                                        className='start-time-group'
                                        value={ fixedStart }
                                        disable={ props.disabled }
                                        onChange={ onTimeChange }
                                        id={`time-input-${props.id}`}/>
                                </div> 
                                : null
                        }
                        
                        <div className={`col day-selector ${(isFixedRange ? 'fixed-mode' : '')}`}>
                            {
                                editDays.map((day : any, idx : number) => 
                                    <div className="day-checkbox" key={idx}>
                                        <input 
                                            readOnly 
                                            type="checkbox" 
                                            id={ day.id } 
                                            checked={ day.selected } 
                                        />
                                        <label onClick={ () => toggleDay(day.dayOfWeek ) } htmlFor={ day.id }>
                                            { day.shortName }
                                        </label>
                                    </div>
                                )
                            }
                        </div>
                    </div>

                    {
                        props.disabled
                            ? null 
                            : <div className="row justify-content-end">
                                <div className="col-auto">
                                    <ConfirmationDialogTrigger
                                            title="Remove this hour set?"
                                            body="Are you sure you want to remove this set of hours?"
                                            successCallback={ () => props.onRemove(props.id) }
                                            triggerText="Remove"                            
                                            triggerIcon="la-trash"
                                            triggerClass="delete-hours-button" />   
                                </div>
                            </div>
                    }
                    {
                        IS_DEBUG  
                            ? <div className="row">
                                <div className="col">
                                    <pre>{ JSON.stringify(friendlyHours, null, 2) }
                                    </pre>
                                </div>
                            </div> 
                            : null
                    }
                </div>                
            </div>
        </React.Fragment>;
};
